import React, { useEffect, useState } from 'react';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { t } from '@lingui/core/macro';
import { getGoogleSheetEmbedUrl } from '../utils/googleSheetEmbed';
import './GoogleSheetView.css';

interface GoogleSheetViewProps {
  googleSheetsUrl: string | null;
}

const ZOOM_STORAGE_KEY = 'e-credits.google-sheet-zoom';

const GoogleSheetView: React.FC<GoogleSheetViewProps> = ({ googleSheetsUrl }) => {
  const [zoom, setZoom] = useState(() => {
    try {
      const storedZoom = Number(window.localStorage.getItem(ZOOM_STORAGE_KEY));
      if (Number.isFinite(storedZoom) && storedZoom >= 50 && storedZoom <= 200) return storedZoom;
    } catch {
      // Storage can be unavailable in private browsing or restricted environments.
    }
    return 100;
  });
  const [zoomInput, setZoomInput] = useState(() => String(zoom));
  const embedUrl = googleSheetsUrl ? getGoogleSheetEmbedUrl(googleSheetsUrl) : null;

  useEffect(() => {
    try {
      window.localStorage.setItem(ZOOM_STORAGE_KEY, String(zoom));
    } catch {
      // Keep the zoom control usable when storage is unavailable.
    }
  }, [zoom]);

  if (!googleSheetsUrl || !embedUrl) {
    return (
      <main className="google-sheet-view google-sheet-empty" aria-live="polite">
        <h1>{t`Google Sheet unavailable`}</h1>
        <p>{t`Load the linked project sheet from the navbar to open it here.`}</p>
      </main>
    );
  }

  return (
    <main className="google-sheet-view">
      <div className="google-sheet-toolbar">
        <label className="google-sheet-zoom">
          <span>{t`Zoom`}</span>
          <input
            type="number"
            min={50}
            max={200}
            step={10}
            value={zoomInput}
            onChange={(event) => {
              setZoomInput(event.target.value);
              const value = event.target.valueAsNumber;
              if (Number.isFinite(value) && value >= 50 && value <= 200) setZoom(value);
            }}
            onBlur={() => {
              const value = zoomInput.trim() === '' ? zoom : Number(zoomInput);
              const nextZoom = Number.isFinite(value) ? Math.min(200, Math.max(50, value)) : zoom;
              setZoom(nextZoom);
              setZoomInput(String(nextZoom));
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.currentTarget.blur();
            }}
          />
          <span aria-hidden="true">%</span>
        </label>
        <a href={googleSheetsUrl} target="_blank" rel="noopener noreferrer" className="workspace-compact-button">
          <OpenInNewRoundedIcon fontSize="small" aria-hidden="true" />
          {t`Open in Google Sheets`}
        </a>
      </div>
      <div
        className="google-sheet-frame-container"
        style={{ '--google-sheet-scale': zoom / 100 } as React.CSSProperties}
      >
        <iframe className="google-sheet-frame" src={embedUrl} title={t`Google Sheet`} />
      </div>
    </main>
  );
};

export default GoogleSheetView;
